import { CheckCircle2, Circle, XCircle } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import CopyToClipboard from "@components/ui-components/copy-to-clipboard";
import StatusChip from "@components/ui-components/status-chip";
import { KYC_LABEL, KYC_TONE } from "@src/utility/constants/panel/common";

const STATUS_ICON = {
    verified: <CheckCircle2 className="h-4 w-4 text-success" />,
    rejected: <XCircle className="h-4 w-4 text-danger" />
};

// One KYC document (`doc` is an entry of toKycSummary().docs): its status and the
// values the publisher entered, so Ops can check them before a force verify.
const KycDocumentRow = ({ doc }) => (
    <li className="py-4 text-sm">
        <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2.5 font-medium text-gray-900">
                {STATUS_ICON[doc.status] || <Circle className={classnames("h-4 w-4", doc.status ? "text-warning" : "text-gray-500")} />}
                {doc.label}
                {doc.optional && <span className="text-xs font-normal text-muted">(optional)</span>}
            </span>
            <StatusChip
                tone={doc.status ? KYC_TONE[doc.status] || "neutral" : "neutral"}
                label={doc.status ? KYC_LABEL[doc.status] || doc.status : "Not submitted"} />
        </div>

        {(doc.fields.length > 0 || doc.originalName) && (
            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 pl-[26px] sm:grid-cols-2">
                {doc.fields.map((field) => (
                    <div key={field.key} className="min-w-0">
                        <dt className="text-xs text-muted">{field.label}</dt>
                        {field.value ? (
                            <dd className="mt-0.5 flex min-w-0 items-center gap-1.5 font-medium text-gray-900">
                                <span className={classnames("truncate", field.copyable && "font-mono tracking-wide")}>{field.value}</span>
                                {field.copyable && <CopyToClipboard text={String(field.value)} width={14} className="shrink-0 cursor-pointer text-muted hover:text-gray-900" />}
                            </dd>
                        ) : (
                            <dd className="mt-0.5 text-xs font-medium text-danger">Missing</dd>
                        )}
                    </div>
                ))}
                {doc.originalName && (
                    <div className="min-w-0">
                        <dt className="text-xs text-muted">Original name</dt>
                        <dd className="mt-0.5 truncate font-medium text-gray-900">{doc.originalName}</dd>
                    </div>
                )}
            </dl>
        )}
    </li>
);

KycDocumentRow.propTypes = {
    doc: PropTypes.shape({
        key         : PropTypes.string.isRequired,
        label       : PropTypes.string.isRequired,
        optional    : PropTypes.bool,
        status      : PropTypes.string,
        originalName: PropTypes.string,
        fields      : PropTypes.arrayOf(PropTypes.shape({
            key     : PropTypes.string.isRequired,
            label   : PropTypes.string.isRequired,
            value   : PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            copyable: PropTypes.bool
        })).isRequired
    }).isRequired
};

export default KycDocumentRow;
